// @ts-nocheck
import { CpuRegisterCollection } from "./internal-registers/cpu-register-collection.js";
import { createCallAndReturnOperations } from "./operations/create-call-and-return-operations.js";
import { createInterruptOperations } from "./operations/create-interupt-operations.js";
import { createJumpOperations } from "./operations/create-jump-operations.js";
import { createRotateShiftOperations } from "./operations/create-rotate-shift-operations.js";
import { createGeneralPurposeOperations } from "./operations/create-general-purpose-operations.js";
import { Cartridge } from "../cartridge/cartridge.js";
import { createCbSubOperations } from "./operations/cb-operations/cb-operation.js";
import { createLogicalOperations } from './operations/create-logical-operations.js';
import { createArithmeticOperations } from './operations/create-arithmetic-operations.js';
import { createInputOutputOperations } from './operations/create-input-output-operations.js';
export class CPU {
    static OperatingHertz = 4_194_304;
    static VBlankInterruptAddress = 0x0040;
    static LCDStatusInterruptAddress = 0x0048;
    static TimerOverflowInterruptAddress = 0x0050;
    static SerialTransferCompletionInterruptAddress = 0x0058;
    static P10P13InputSignalLowInterruptAddress = 0x0060;
    isInterruptMasterEnable = true;
    registers;
    // Pre-step execution trap (MultiNostalgia patch — see ../../PATCHES.md).
    // When set, fires once per instruction with the PC about to execute,
    // AFTER interrupt dispatch (so the PC reflects a vector redirect) and
    // only when the CPU is actually stepping (not halted). A handler may
    // write registers/PC to supersede the trapped instruction. Installed by
    // GbCoreBase only while ≥1 exec hook is armed, so the no-hook path is
    // untouched.
    onExecute;
    operations = [];
    cbSubOperationMap = [];
    // To keep the cpu file focused on operation and to organize the operations by type, separate methods have
    // been created in the /operations folder and are attached here.
    createInputOutputOperations = createInputOutputOperations;
    createArithmeticOperations = createArithmeticOperations;
    createLogicalOperations = createLogicalOperations;
    createRotateShiftOperations = createRotateShiftOperations;
    createJumpOperations = createJumpOperations;
    createGeneralPurposeOperations = createGeneralPurposeOperations;
    createCallAndReturnOperations = createCallAndReturnOperations;
    createInterruptOperations = createInterruptOperations;
    createCbSubOperations = createCbSubOperations;
    isHalted = false;
    isStopped = false;
    memory;
    interruptController;
    timerController;
    constructor(bus, interruptController, timerController) {
        this.memory = bus;
        this.interruptController = interruptController;
        this.timerController = timerController;
        this.registers = new CpuRegisterCollection();
        this.createInputOutputOperations();
        this.createArithmeticOperations();
        this.createLogicalOperations();
        this.createRotateShiftOperations();
        this.createJumpOperations();
        this.createGeneralPurposeOperations();
        this.createCallAndReturnOperations();
        this.createInterruptOperations();
        this.createCbSubOperations();
        this.initialize();
    }
    initialize() {
        this.registers.programCounter.value = Cartridge.EntryPointOffset;
        this.registers.stackPointer.value = 0xfffe;
        this.registers.HL.value = 0x014d;
        this.registers.C.value = 0x13;
        this.registers.E.value = 0xD8;
        this.registers.A.value = 1;
        this.registers.F.value = 0xb0;
    }
    tick() {
        this.handleInterrupts();
        if (this.isHalted) {
            this.timerController.updateTimers(4);
            return 4;
        }
        if (this.onExecute)
            this.onExecute(this.registers.programCounter.value);
        const operation = this.getOperation();
        operation.execute();
        this.timerController.updateTimers(operation.cycleTime);
        return operation.cycleTime;
    }
    reset() {
        this.registers.reset();
    }
    halt() {
        this.isHalted = true;
    }
    stop() {
        this.isStopped = true;
        this.timerController.writeDiv();
    }
    pushToStack(word) {
        this.registers.stackPointer.value -= 2;
        this.memory.writeWord(this.registers.stackPointer.value, word);
    }
    popFromStack() {
        const value = this.memory.readWord(this.registers.stackPointer.value);
        this.registers.stackPointer.value += 2;
        return value;
    }
    getOperation() {
        const operationIndex = this.memory.readByte(this.registers.programCounter.value);
        this.registers.programCounter.value++;
        const operation = this.operations[operationIndex];
        if (!operation) {
            const opCode = operationIndex.toString(16);
            const address = this.registers.programCounter.value.toString(16);
            throw new Error(`Operation ${opCode} at location ${address} not found.`);
        }
        return operation;
    }
    getInterruptEnableRegisterValue() {
        return this.memory.readByte(0xffff);
    }
    handleInterrupts() {
        const firedInterrupts = this.interruptController.value & this.getInterruptEnableRegisterValue();
        if (firedInterrupts > 0) {
            this.isHalted = false;
        }
        if (!this.isInterruptMasterEnable || firedInterrupts === 0) {
            return;
        }
        this.pushToStack(this.registers.programCounter.value);
        const interruptFlags = this.interruptController.getInterruptFlags(firedInterrupts);
        if (interruptFlags.isVerticalBlanking) {
            this.interruptController.clearVBlankInterruptRequest();
            this.registers.programCounter.value = CPU.VBlankInterruptAddress;
        }
        else if (interruptFlags.isLCDStatus) {
            this.interruptController.clearLcdStatusInterruptRequest();
            this.registers.programCounter.value = CPU.LCDStatusInterruptAddress;
        }
        else if (interruptFlags.isTimerOverflow) {
            this.interruptController.clearTimerOverflowInterruptRequest();
            this.registers.programCounter.value = CPU.TimerOverflowInterruptAddress;
        }
        else if (interruptFlags.isSerialTransferCompletion) {
            this.interruptController.clearSerialTransferInterruptRequest();
            this.registers.programCounter.value = CPU.SerialTransferCompletionInterruptAddress;
        }
        else if (interruptFlags.isP10P13NegativeEdge) {
            this.interruptController.clearP10P13NegativeEdgeInterruptRequest();
            this.registers.programCounter.value = CPU.P10P13InputSignalLowInterruptAddress;
        }
        this.isInterruptMasterEnable = false;
    }
    addOperation(operation) {
        if (this.operations[operation.byteDefinition]) {
            throw new Error(`Operation ${operation.byteDefinition.toString(2)} has already been defined`);
        }
        this.operations[operation.byteDefinition] = operation;
    }
    addCbOperation(operation) {
        if (this.cbSubOperationMap[operation.byteDefinition]) {
            throw new Error(`Operation ${operation.byteDefinition.toString(2)} has already been defined`);
        }
        this.cbSubOperationMap[operation.byteDefinition] = operation;
    }
}
//# sourceMappingURL=cpu.js.map